import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  MessageCircle, X, Send, Bot, User as UserIcon, Loader2, Sparkles,
} from "lucide-react";

const NAVY = "#0b192c", GOLD = "#c4a454", MUTED = "#94a3b8", BORDER = "#e2e1dd";

// Lightweight markdown → HTML for assistant replies. Handles the exact subset
// we ask the model to produce: **bold**, `inline code`, `- ` bullets,
// paragraph breaks. Escapes user-facing HTML first so replies can't inject
// tags. Kept in-file so we don't add a react-markdown dependency.
function escapeHtml(s = "") {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;")
                  .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function renderMarkdown(src = "") {
  const esc = escapeHtml(src);
  const lines = esc.split(/\r?\n/);
  const out = [];
  let inList = false;
  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${bullet[1]}</li>`);
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      if (line.trim()) out.push(`<p>${line}</p>`);
    }
  }
  if (inList) out.push("</ul>");
  return out.join("")
    .replace(/\*\*([^*\n]+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`\n]+?)`/g, "<code>$1</code>");
}

const ROLE_STARTERS = {
  owner: [
    "How do I approve a fund?",
    "How do I invite a new practice's owner?",
    "How do I run the PAN sweep?",
  ],
  super_admin: [
    "How do I grant fund-approval permission to a Sub Admin?",
    "How do I add a new AIF client and its first fund?",
    "How do I mandate 2FA for the whole practice?",
  ],
  fund_admin: [
    "How do I submit a fund for approval?",
    "Why can't I see a client I just created?",
    "Where do I upload the SEBI filing?",
  ],
  accountant: [
    "How do I post a journal entry?",
    "How does the NAV approval flow work?",
    "How do I reconcile the bank statement?",
  ],
  compliance_officer: [
    "How do I close a compliance item on the calendar?",
    "Where is the audit trail?",
    "How do I run the PAN sweep?",
  ],
  fund_manager: [
    "Where do I see my portfolio holdings?",
    "How do I add a deal to the workflow?",
    "How do I export analytics?",
  ],
  investor: [
    "How do I download my latest statement?",
    "How do I complete KYC?",
    "Where do I see my capital calls?",
  ],
  distributor: [
    "Where is my commission summary?",
    "How do I update my payout details?",
  ],
};

function starterFor(user) {
  if (!user) return ROLE_STARTERS.investor;
  if (user.is_platform_admin) return ROLE_STARTERS.owner;
  const roles = user.roles || [];
  for (const key of ["super_admin", "fund_admin", "accountant",
                      "compliance_officer", "fund_manager"]) {
    if (roles.includes(key)) return ROLE_STARTERS[key];
  }
  return ROLE_STARTERS[user.active_role] || ROLE_STARTERS.investor;
}

function personaLabel(persona) {
  switch (persona) {
    case "owner": return "Owner Assistant";
    case "super_admin": return "Admin Assistant";
    case "fund_admin": return "Fund-admin Assistant";
    case "accountant": return "Accountant Assistant";
    case "compliance_officer": return "Compliance Assistant";
    case "fund_manager": return "Fund-manager Assistant";
    case "relationship_manager": return "RM Assistant";
    case "investor": return "LP Assistant";
    case "distributor": return "Distributor Assistant";
    default: return "AIFLedger Assistant";
  }
}

/**
 * AIFLedger Assistant — role-aware helper bot for every logged-in user.
 * Backed by Claude Sonnet 4.6 via the Emergent LLM key. The system prompt
 * is composed server-side from the caller's roles so an LP can never see
 * admin instructions, and an admin never gets bored with LP-portal steps.
 */
export default function HelperBot() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);       // [{role:'user'|'assistant', text, at}]
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [persona, setPersona] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [open, msgs.length, sending]);

  const starters = useMemo(() => starterFor(user), [user]);

  // Don't render for anonymous / login pages, or demo prospects (the sales
  // `ChatWidget` already owns the corner for them). Hooks above stay in a
  // deterministic order — the early return sits AFTER every useState /
  // useEffect / useMemo call.
  const isDemo = !!user?.is_demo && !!user?.demo_id;
  if (!user || isDemo) return null;

  const send = async (payload) => {
    const trimmed = (payload || text).trim();
    if (!trimmed || sending) return;
    setSending(true);
    const now = new Date().toISOString();
    setMsgs((m) => [...m, { role: "user", text: trimmed, at: now }]);
    setText("");
    try {
      const { data } = await api.post("/helper-bot/chat", {
        text: trimmed,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setPersona(data.persona);
      setMsgs((m) => [...m, { role: "assistant", text: data.answer,
                              at: new Date().toISOString() }]);
    } catch (e) {
      const detail = e?.response?.data?.detail
                  || "Assistant is unavailable right now. Please try again shortly.";
      setMsgs((m) => [...m, { role: "assistant", text: detail,
                              at: new Date().toISOString(), errored: true }]);
    } finally {
      setSending(false);
    }
  };

  // Detect "open /route" hints in Assistant replies and offer a jump button.
  const routeFromReply = (t) => {
    const m = (t || "").match(/(\/[a-z][a-z0-9/_?=&-]*)/i);
    if (!m) return null;
    const route = m[1].split("?")[0].split("#")[0];
    // Only trust in-app routes we actually ship.
    if (!/^\/[a-z]/.test(route)) return null;
    return route;
  };

  return (
    <div className="fixed bottom-6 left-6 z-[70]" data-testid="helper-bot">
      {open ? (
        <div className="w-[380px] h-[560px] max-h-[85vh] rounded-xl shadow-2xl flex flex-col overflow-hidden bg-white"
             style={{ border: `1px solid ${BORDER}` }}>
          <div className="px-4 py-3 flex items-center justify-between"
               style={{ background: NAVY, color: "white" }}>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1"
                   style={{ color: GOLD }}>
                <Sparkles size={11} /> {personaLabel(persona)}
              </div>
              <div className="text-sm font-serif">Ask anything about AIFLedger.</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10"
                    aria-label="Close" data-testid="helper-bot-close">
              <X size={16} />
            </button>
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3"
               style={{ background: "#faf9f5" }} data-testid="helper-bot-thread">
            {msgs.length === 0 && (
              <div className="space-y-2">
                <div className="text-xs" style={{ color: MUTED }}>
                  Hi <b>{user.name || user.email}</b>. I know AIFLedger cover-to-cover
                  for the role you hold. Try one of these — or type your own question:
                </div>
                {starters.map((s, i) => (
                  <button key={i} type="button" onClick={() => send(s)}
                          className="w-full text-left px-3 py-2 rounded border text-xs hover:bg-white transition"
                          style={{ borderColor: BORDER, color: NAVY, background: "white" }}
                          data-testid={`helper-bot-starter-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            )}
            {msgs.map((m, idx) => {
              const mine = m.role === "user";
              const routeHint = !mine ? routeFromReply(m.text) : null;
              return (
                <div key={idx} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[85%] px-3 py-2 rounded-lg text-xs leading-relaxed break-words whitespace-pre-wrap"
                       style={{
                         background: mine ? NAVY : "white",
                         color: mine ? "white" : NAVY,
                         border: mine ? "none" : `1px solid ${BORDER}`,
                         opacity: m.errored ? 0.85 : 1,
                       }}
                       data-testid={`helper-bot-msg-${m.role}`}>
                    {!mine && (
                      <div className="text-[9px] uppercase tracking-widest font-semibold mb-0.5 flex items-center gap-1"
                           style={{ color: GOLD }}>
                        <Bot size={9} /> Assistant
                      </div>
                    )}
                    {mine && (
                      <div className="text-[9px] uppercase tracking-widest font-semibold mb-0.5 flex items-center gap-1 opacity-70">
                        <UserIcon size={9} /> You
                      </div>
                    )}
                    {!mine ? (
                      <div className="markdown-body" style={{ fontSize: "0.75rem", lineHeight: "1.25rem" }}
                           dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />
                    ) : (
                      <div>{m.text}</div>
                    )}
                    {routeHint ? (
                      <button type="button" onClick={() => { setOpen(false); navigate(routeHint); }}
                              className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold underline decoration-dotted"
                              style={{ color: GOLD }}
                              data-testid="helper-bot-jump">
                        Open {routeHint} →
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {sending && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-lg text-xs flex items-center gap-2"
                     style={{ background: "white", border: `1px solid ${BORDER}`, color: NAVY }}
                     data-testid="helper-bot-thinking">
                  <Loader2 size={12} className="animate-spin" style={{ color: GOLD }} />
                  Assistant is thinking…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }}
                className="p-2 border-t flex gap-2 bg-white items-center"
                style={{ borderColor: BORDER }}>
            <input value={text} onChange={(e) => setText(e.target.value)}
                   placeholder="Type your question…" maxLength={2000}
                   className="flex-1 border rounded px-3 py-1.5 text-xs outline-none focus:border-[#c4a454]"
                   style={{ borderColor: BORDER, color: NAVY }}
                   data-testid="helper-bot-input" />
            <button type="submit" disabled={sending || !text.trim()}
                    className="px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-40"
                    style={{ background: NAVY, color: "white" }}
                    data-testid="helper-bot-send">
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            </button>
          </form>
        </div>
      ) : (
        <button onClick={() => setOpen(true)}
                className="w-14 h-14 rounded-full shadow-xl flex items-center justify-center hover:scale-105 transition relative"
                style={{ background: NAVY, color: "white" }}
                aria-label="Open Assistant" data-testid="helper-bot-open"
                title="AIFLedger Assistant — role-aware in-app help">
          <MessageCircle size={22} />
          <span className="absolute -top-1 -right-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-widest"
                style={{ background: GOLD, color: NAVY }}>AI</span>
        </button>
      )}
    </div>
  );
}
